import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid2 as Grid,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import clsx from 'clsx';
import { useState } from 'react';
import { COMPONENT_TYPES_ICONS } from '@/admins/SmartReports/constants';
import { getThumbnailComponent } from '@/admins/SmartReports/helpers';
import { useGetComponents } from '@/admins/SmartReports/hooks/useGetComponents';
import { SmartReportComponentTypesEnum } from '@/admins/SmartReports/types';
import { DragContainer } from '@/components/DragAndDrop/DragContainer';
import { SmartReportLayouts } from '../Layouts';
import { useEditSmartReport } from './EditSmartReportContext';

const useStyles = makeStyles(theme => ({
  img: {
    margin: '10px',
    width: '50%',
    border: `solid thin ${theme.palette.baseColors.gray.c100}`,
    cursor: 'pointer',
  },
  componentImg: {
    margin: '0',
    width: '100%',
    border: 'none',
    objectFit: 'contain',
    objectPosition: 'center',
  },
  number: {
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'Barlow',
  },
  selected: {
    border: 'none',
    outline: `solid 3px ${theme.palette.baseColors.infoBlue.c100}`,
  },
  horizontalScroll: {
    height: 'fit-content',
    width: '100%',
    overflow: 'scroll',
    overflowY: 'hidden',
    display: 'flex',
  },
  applyButton: {
    width: 'fit-content',
    alignSelf: 'flex-end',
    borderRadius: '2em',
    padding: '0.5em 2em',
    textTransform: 'none',
    maxHeight: 'fit-content',
    boxShadow: 'none',
  },
  transparent: { backgroundColor: 'transparent', color: 'black' },
  componentGrid: {
    borderTop: 'solid thin lightgrey',
    display: 'grid',
    paddingTop: '.5rem',
    gridTemplateColumns: '1fr 1fr',
    gap: '.5rem',
  },
}));

const LayoutItem = ({ layout }) => {
  const { portfolio, selectedPage, applyLayout } = useEditSmartReport();

  const LayoutComponentThumbnail = layout?.Thumbnail;
  return (
    <Tooltip title={layout.title} placement='top' arrow>
      <span>
        <LayoutComponentThumbnail
          sx={{
            aspectRatio: '16 / 9',
            margin: '10px',
            minWidth: '200px',
            height: 'auto',
            border: theme => `solid thin ${theme.palette.baseColors.gray.c100}`,
            cursor: 'pointer',
            ...(selectedPage?.layout === layout.id && {
              border: theme => `solid 3px ${theme.palette.baseColors.infoBlue.c100}`,
            }),
          }}
          portfolio={portfolio}
          onClick={() => applyLayout(layout)}
        />
      </span>
    </Tooltip>
  );
};

const ComponentItem = ({ title, Icon, components }) => {
  const classes = useStyles();
  const { portfolio } = useEditSmartReport();
  const [expanded, setExpanded] = useState(true);

  if (components.length === 0) {
    return null;
  }

  return (
    <Accordion
      className={classes.transparent}
      style={{ width: '100%' }}
      square={true}
      expanded={expanded}
      onChange={(_, exp) => setExpanded(exp)}
    >
      <AccordionSummary
        expandIcon={<GridExpandMoreIcon className={classes.transparent} />}
        aria-controls='panel-layers'
      >
        <Box display='flex' alignItems='center'>
          <Icon sx={{ fontSize: 24, marginRight: 1 }} />
          <Typography variant='subtitle2'>{title}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails className={classes.transparent} style={{ padding: 0 }}>
        <div className={classes.componentGrid}>
          {components.map((item, idx) => {
            const ThumbnailComponent = getThumbnailComponent(item);

            return (
              <div key={`smart-reports-component-item-components-${item.type}-${idx}`}>
                <DragContainer item={item}>
                  <ThumbnailComponent
                    sx={{ fontSize: '1.5em' }}
                    portfolio={portfolio}
                    className={clsx(classes.img, classes.componentImg)}
                  />
                  <Typography variant='subtitle2'>{item.name}</Typography>
                </DragContainer>
              </div>
            );
          })}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

export const ComponentsList = () => {
  const { report, selectedLayout } = useEditSmartReport();

  const { components: textFieldComponents } = useGetComponents({
    type: SmartReportComponentTypesEnum.TextField,
    reportType: report?.smartReportType?.name,
    layout: selectedLayout,
  });

  const { components: imageComponents } = useGetComponents({
    type: SmartReportComponentTypesEnum.Image,
    reportType: report?.smartReportType?.name,
    layout: selectedLayout,
  });

  const { components: textAreaComponents } = useGetComponents({
    type: SmartReportComponentTypesEnum.TextArea,
    reportType: report?.smartReportType?.name,
    layout: selectedLayout,
  });

  const { components: chartComponents } = useGetComponents({
    type: SmartReportComponentTypesEnum.Chart,
    reportType: report?.smartReportType?.name,
    layout: selectedLayout,
  });

  const { components: tableComponents } = useGetComponents({
    type: SmartReportComponentTypesEnum.Table,
    reportType: report?.smartReportType?.name,
    layout: selectedLayout,
  });

  return (
    <Grid container spacing={2}>
      <Grid item size={12} key={`smart-reports-components-text-fields`}>
        <ComponentItem
          title={'Text Fields'}
          Icon={COMPONENT_TYPES_ICONS[SmartReportComponentTypesEnum.TextField]}
          components={textFieldComponents}
        />
        <ComponentItem
          title={'Text Areas'}
          Icon={COMPONENT_TYPES_ICONS[SmartReportComponentTypesEnum.TextArea]}
          components={textAreaComponents}
        />
        <ComponentItem
          title={'Images'}
          Icon={COMPONENT_TYPES_ICONS[SmartReportComponentTypesEnum.Image]}
          components={imageComponents}
        />

        <ComponentItem
          title={'Charts'}
          Icon={COMPONENT_TYPES_ICONS[SmartReportComponentTypesEnum.Chart]}
          components={chartComponents}
        />
        <ComponentItem
          title={'Tables'}
          Icon={COMPONENT_TYPES_ICONS[SmartReportComponentTypesEnum.Table]}
          components={tableComponents}
        />
      </Grid>
    </Grid>
  );
};

export const SideBar = () => {
  const classes = useStyles();
  const { report, entity } = useEditSmartReport();

  const layouts = SmartReportLayouts.filter(
    layout => !layout.reportTypes || layout.reportTypes.includes(report?.smartReportType?.name),
  );

  return (
    <Grid container spacing={2} padding={3}>
      <Grid item size={12} display='flex' justifyContent='space-between'>
        <Typography variant='subtitle2'>Entities</Typography>
        <Typography variant='subtitle2' fontWeight='bold' sx={{ textDecoration: 'underline' }}>
          {entity?.name}
        </Typography>
      </Grid>
      <Grid item size={12} display='flex' justifyContent='space-between'>
        <Typography variant='subtitle2'>Layout</Typography>
      </Grid>
      <Grid item size={12} display='flex' justifyContent='flex-end' flexDirection='column'>
        <Grid className={classes.horizontalScroll} item size={12} display='flex'>
          {layouts.map((layout, idx) => (
            <LayoutItem key={`smart-reports-layout-item-${layout.name}-${idx}`} layout={layout} />
          ))}
          <Box sx={{ width: 0, height: '100%' }} />
        </Grid>
      </Grid>

      <Grid item size={12} display='flex' justifyContent='space-between'>
        <Typography variant='subtitle2'>Components</Typography>
      </Grid>
      <Grid item size={12} display='flex' flexDirection='column'>
        <ComponentsList />
      </Grid>
    </Grid>
  );
};
